import {
  ShieldIcon,
  EyeIcon,
  EyeOffIcon,
  MailIcon,
  Loader2Icon,
  KeyRoundIcon,
  RefreshCwIcon,
  XIcon,
  SmartphoneIcon,
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'

import { apiClient } from '@nebula/shared/services'
import { otpApi, setTrustedDeviceToken } from '@nebula/shared/services/otpApi'
import { useAuthStore, OTPRequiredError } from '@nebula/shared/store'
import { getApiErrorText, getErrorMessage } from '@nebula/shared/utils'

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const { login, completeOTPLogin } = useAuthStore()

  // OTP state
  const [showOTPModal, setShowOTPModal] = useState(false)
  const [otpCode, setOTPCode] = useState('')
  const [tempToken, setTempToken] = useState('')
  const [otpLoading, setOTPLoading] = useState(false)
  const [otpResendLoading, setOTPResendLoading] = useState(false)
  const [otpMethod, setOtpMethod] = useState<'totp' | 'email'>('email')
  const [emailFallback, setEmailFallback] = useState(false)
  const [rememberDevice, setRememberDevice] = useState(false)
  const otpInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (showOTPModal && otpInputRef.current) {
      otpInputRef.current.focus()
    }
  }, [showOTPModal])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setShowVerificationPrompt(false)

    try {
      await login(email, password)
      toast.success('Login realizado com sucesso!')
    } catch (error: any) {
      if (error instanceof OTPRequiredError) {
        setTempToken(error.temp_token)
        setOTPCode('')
        setOtpMethod(error.method === 'totp' ? 'totp' : 'email')
        setEmailFallback(error.email_fallback)
        setRememberDevice(false)
        setShowOTPModal(true)
        toast.info(error.message)
      } else {
        const errorMessage = getErrorMessage(error)
        if (error?.response?.data?.error?.requires_email_verification) {
          setShowVerificationPrompt(true)
        }
        toast.error(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleOTPVerify = async () => {
    if (otpCode.trim().length < 6) return
    setOTPLoading(true)
    try {
      const response = await otpApi.verify({
        temp_token: tempToken,
        otp_code: otpCode.trim(),
        remember_device: rememberDevice,
      })
      if (response.trusted_device_token) setTrustedDeviceToken(response.trusted_device_token)
      await completeOTPLogin(response.access_token, response.refresh_token)
      setShowOTPModal(false)
      toast.success('Login realizado com sucesso!')
    } catch (error: any) {
      toast.error(getApiErrorText(error) || 'Código inválido')
      setOTPCode('')
      otpInputRef.current?.focus()
    } finally {
      setOTPLoading(false)
    }
  }

  const handleOTPResend = async () => {
    setOTPResendLoading(true)
    try {
      const response = await otpApi.resend({ temp_token: tempToken })
      setTempToken(response.temp_token)
      setOTPCode('')
      setOtpMethod('email')
      toast.success('Novo código enviado para seu email.')
      otpInputRef.current?.focus()
    } catch (error: any) {
      toast.error(getApiErrorText(error) || 'Erro ao reenviar código')
    } finally {
      setOTPResendLoading(false)
    }
  }

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Por favor, insira seu email primeiro')
      return
    }

    setResendLoading(true)
    try {
      await apiClient.post('/api/v1/auth/resend-verification', {
        email,
        frontend_url: window.location.origin,
      })
      toast.success('Email de verificação reenviado! Verifique sua caixa de entrada.')
    } catch (error: any) {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage)
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-gray-900 via-indigo-900 to-gray-900">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-lg">
          <div className="mb-8 flex justify-center">
            <div className="rounded-full bg-indigo-600/20 p-4">
              <ShieldIcon className="size-12 text-indigo-400" />
            </div>
          </div>

          <h1 className="mb-2 text-center text-2xl font-bold text-white">Cloud Reseller</h1>
          <p className="mb-8 text-center text-gray-300">Portal para Revendedores</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-200">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                placeholder="admin@email.com"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-200">Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 pr-12 text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? (
                    <EyeOffIcon className="size-5" />
                  ) : (
                    <EyeIcon className="size-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-indigo-600 py-3 font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">Acesso restrito - Portal para Revendedores</p>
          </div>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showVerificationPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-yellow-500/50 bg-gray-900 p-6 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="shrink-0 rounded-full bg-yellow-500/20 p-3">
                <MailIcon className="size-6 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="mb-2 text-lg font-semibold text-white">Email não verificado</h3>
                <p className="mb-4 text-sm text-gray-300">
                  Seu email ainda não foi verificado. Verifique sua caixa de entrada ou solicite um
                  novo email de verificação.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-yellow-600 px-4 py-2 font-medium text-white hover:bg-yellow-700 disabled:opacity-50"
                  >
                    {resendLoading ? (
                      <>
                        <Loader2Icon className="size-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <MailIcon className="size-4" />
                        Reenviar email
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowVerificationPrompt(false)}
                    className="rounded-lg bg-gray-700 px-4 py-2 font-medium text-gray-300 hover:bg-gray-600"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTP Modal */}
      {showOTPModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-indigo-500/50 bg-gray-900 p-6 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-indigo-500/20 p-3">
                  {otpMethod === 'totp' ? (
                    <SmartphoneIcon className="size-6 text-indigo-400" />
                  ) : (
                    <KeyRoundIcon className="size-6 text-indigo-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Verificação em duas etapas</h3>
                  <p className="text-sm text-gray-400">
                    {otpMethod === 'totp'
                      ? 'Digite o código do seu app autenticador'
                      : 'Enviamos um código para seu email'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowOTPModal(false)
                  setOTPCode('')
                  setTempToken('')
                }}
                className="p-1 text-gray-400 hover:text-white"
              >
                <XIcon className="size-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">
                  {otpMethod === 'totp'
                    ? 'Código do app (ou de recuperação)'
                    : 'Código de 6 dígitos'}
                </label>
                <input
                  ref={otpInputRef}
                  type="text"
                  inputMode={otpMethod === 'totp' ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOTPCode(e.target.value.replace(/\s/g, '').slice(0, 19))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && otpCode.trim().length >= 6) handleOTPVerify()
                  }}
                  maxLength={19}
                  className="w-full rounded-lg border border-white/20 bg-white/10 p-4 text-center font-mono text-2xl tracking-widest text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="000000"
                />
              </div>

              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-300 select-none">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="rounded border-white/30 bg-white/10"
                />
                Confiar neste dispositivo por 30 dias
              </label>

              <button
                onClick={handleOTPVerify}
                disabled={otpLoading || otpCode.trim().length < 6}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {otpLoading ? (
                  <>
                    <Loader2Icon className="size-4 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Verificar'
                )}
              </button>
              <div className="flex items-center justify-between pt-2">
                {otpMethod === 'totp' ? (
                  emailFallback ? (
                    <button
                      onClick={handleOTPResend}
                      disabled={otpResendLoading}
                      className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
                    >
                      {otpResendLoading ? (
                        <Loader2Icon className="size-3 animate-spin" />
                      ) : (
                        <MailIcon className="size-3" />
                      )}
                      Receber código por email
                    </button>
                  ) : (
                    <span />
                  )
                ) : (
                  <button
                    onClick={handleOTPResend}
                    disabled={otpResendLoading}
                    className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
                  >
                    {otpResendLoading ? (
                      <Loader2Icon className="size-3 animate-spin" />
                    ) : (
                      <RefreshCwIcon className="size-3" />
                    )}
                    Reenviar código
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowOTPModal(false)
                    setOTPCode('')
                    setTempToken('')
                  }}
                  className="text-sm text-gray-400 hover:text-gray-300"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LoginPage
